import './reactive-broker-brokerage.scss';

import { Box, Stack, Typography } from '@mui/material';
import {
  DialogLayout,
  NewConvergeButton,
} from '../../../../ui/src/presentation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useConvergeNavigation, useConvergeSnackBar } from '../../contexts';

import { ApolloError } from '@apollo/client';
import { CreateBrokerageFields } from './commission-container';
import { capitalizeFirstLetter } from '@converge/data';
import { useCallback } from 'react';
import { useUpsertBrokerageMutation } from '@converge/graphql';

export const ReactiveBrokerBrokerage = ({
  onClose,
  entityUuid,
  personUuid,
  open,
  isBrokerage,
}: {
  onClose?: () => void;
  entityUuid?: string;
  open: boolean;
  personUuid?: string;
  isBrokerage?: boolean;
}) => {
  const { upsertEntity, loading } = useUpsertBrokerageMutation();
  const { notify } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();
  const formMethods = useForm<CreateBrokerageFields>({
    defaultValues: {},
    mode: 'all',
  });
  const name = isBrokerage ? 'brokerage' : 'broker';

  const onSubmitStatusHandler = useCallback(
    (): SubmitHandler<CreateBrokerageFields> => async (fields) => {
      let brokerResponse, brokerResponseErrors;

      const brokerageData = {
        status: 'ACTIVE',
        entityUuid: entityUuid,
      } as unknown;

      const brokerData = {
        entityUuid: entityUuid,
        persons: [{ personUuid: personUuid, status: 'ACTIVE' }],
      } as unknown;

      const updateName = name.charAt(0).toUpperCase() + name.slice(1);

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: isBrokerage ? brokerageData : brokerData },
        });

        brokerResponse = data;
        brokerResponseErrors = errors;

        notify(`${updateName} has been Reactivated`, {
          variant: 'success',
        });

        if (isBrokerage) {
          navigate.producer();
        } else {
          navigate.producerBrokers();
        }
      } catch (cause) {
        console.error(`Failed to update ${name}: ${cause}`);
        if (cause instanceof ApolloError) {
          brokerResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = brokerResponseErrors?.[0]?.message;
      if (errorMessage || !brokerResponse) {
        notify(
          `Failed to update ${name} : ${
            errorMessage || 'No data returned by the API'
          }`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [entityUuid, navigate, notify, upsertEntity, isBrokerage, name, personUuid]
  );

  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      sx={{
        minWidth: '600px',
        '& .MuiPaper-root.MuiDialog-paper': {
          maxWidth: '600px',
          minWidth: '600px',
          overflow: 'hidden',
        },
      }}
      className="reactive-modal"
      header={
        <Box className="modal-title-2">
          <p
            style={{
              margin: 0,
              color: 'black',
              fontSize: '24px',
            }}
          >{`Reactivate ${capitalizeFirstLetter(name)}`}</p>
        </Box>
      }
    >
      <Stack>
        <Box>
          <Typography className="modal-message">
            Are you sure you want to reactivate this {`${name}`}?
          </Typography>
        </Box>
      </Stack>

      <Stack className="button-container-modal">
        <Box className="close-btn-Container">
          <NewConvergeButton
            label="CANCEL"
            variant="outlined"
            onClick={onClose}
            data-testid="cancel-brokerage-license-btn"
          />
        </Box>
        <Box>
          <NewConvergeButton
            loading={loading}
            onClick={formMethods.handleSubmit(onSubmitStatusHandler())}
            data-testid={`reactivate-${name}-btn`}
            label={`Reactivate ${name}`}
          />
        </Box>
      </Stack>
    </DialogLayout>
  );
};
